/**
 * ═══════════════════════════════════════════════════════════════
 *  AS CONSULTECH — PREMIUM ANIMATION SYSTEM
 *  Scroll-driven parallax, staggered reveals, smooth transitions
 *  Built with Framer Motion + Intersection Observer
 * ═══════════════════════════════════════════════════════════════
 */
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";

/* ═══════════════════════════════════════════
   1. SCROLL-DRIVEN PARALLAX
   Returns a spring-animated transform value 
   that moves an element relative to scroll.
═══════════════════════════════════════════ */
export function useParallax(speed = 0.15, springConfig = { stiffness: 50, damping: 20 }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [-100 * speed, 100 * speed]);
  const springY = useSpring(y, springConfig);
  return { ref, y: springY, progress: scrollYProgress };
}

/* ═══════════════════════════════════════════
   2. SCROLL-DRIVEN OPACITY + SCALE
   Fades in and scales up as element scrolls 
   into the viewport center.
═══════════════════════════════════════════ */
export function useScrollReveal(threshold = 0.1) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0.6]);
  const scale = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0.92, 1, 1, 0.98]);
  return { ref, opacity, scale, progress: scrollYProgress };
}

/* ═══════════════════════════════════════════
   3. MOUSE PARALLAX (for entire sections)
   Track mouse position and create subtle 
   parallax shift for background elements.
═══════════════════════════════════════════ */
export function useMouseParallax(strength = 20) {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const springX = useSpring(mouseX, { stiffness: 40, damping: 15 });
  const springY = useSpring(mouseY, { stiffness: 40, damping: 15 });

  const handleMouse = useCallback((e) => {
    const { innerWidth, innerHeight } = window;
    const x = (e.clientX / innerWidth - 0.5) * strength;
    const y = (e.clientY / innerHeight - 0.5) * strength;
    mouseX.set(x);
    mouseY.set(y);
  }, [mouseX, mouseY, strength]);

  useEffect(() => {
    window.addEventListener("mousemove", handleMouse, { passive: true });
    return () => window.removeEventListener("mousemove", handleMouse);
  }, [handleMouse]);

  return { x: springX, y: springY };
}

/* ═══════════════════════════════════════════
   4. STAGGER CONTAINER VARIANTS
   Pre-built Framer Motion variants for 
   staggered children animations.
═══════════════════════════════════════════ */
export const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
};

export const staggerItem = {
  hidden: { opacity: 0, y: 30, filter: "blur(6px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: {
      duration: 0.7,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

export const staggerItemFromLeft = {
  hidden: { opacity: 0, x: -40, filter: "blur(4px)" },
  visible: {
    opacity: 1,
    x: 0,
    filter: "blur(0px)",
    transition: {
      duration: 0.7,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

export const staggerItemFromRight = {
  hidden: { opacity: 0, x: 40, filter: "blur(4px)" },
  visible: {
    opacity: 1,
    x: 0,
    filter: "blur(0px)",
    transition: {
      duration: 0.7,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

export const staggerItemScale = {
  hidden: { opacity: 0, scale: 0.85, filter: "blur(8px)" },
  visible: {
    opacity: 1,
    scale: 1,
    filter: "blur(0px)",
    transition: {
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

/* ═══════════════════════════════════════════
   5. PAGE TRANSITION VARIANTS
   Smooth entry/exit for route transitions.
═══════════════════════════════════════════ */
export const pageTransition = {
  initial: { opacity: 0, y: 20, filter: "blur(8px)" },
  animate: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: {
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1],
    },
  },
  exit: {
    opacity: 0,
    y: -10,
    filter: "blur(4px)",
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 1, 1],
    },
  },
};

/* ═══════════════════════════════════════════
   6. HERO ENTRANCE VARIANTS
   Cinematic staggered entrance for hero sections.
═══════════════════════════════════════════ */
export const heroVariants = {
  eyebrow: {
    initial: { opacity: 0, x: -30, filter: "blur(4px)" },
    animate: {
      opacity: 1,
      x: 0,
      filter: "blur(0px)",
      transition: { delay: 0.2, duration: 0.7, ease: [0.16, 1, 0.3, 1] },
    },
  },
  title: {
    initial: { opacity: 0, y: 40, filter: "blur(10px)" },
    animate: {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      transition: { delay: 0.35, duration: 0.9, ease: [0.16, 1, 0.3, 1] },
    },
  },
  subtitle: {
    initial: { opacity: 0, y: 25, filter: "blur(6px)" },
    animate: {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      transition: { delay: 0.55, duration: 0.7, ease: [0.16, 1, 0.3, 1] },
    },
  },
  cta: {
    initial: { opacity: 0, y: 20, scale: 0.95 },
    animate: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { delay: 0.75, duration: 0.6, ease: [0.16, 1, 0.3, 1] },
    },
  },
};

/* ═══════════════════════════════════════════
   7. SECTION HEADING REVEAL
   Scroll-triggered headline with mask reveal.
═══════════════════════════════════════════ */
export const headingReveal = {
  initial: { opacity: 0, y: 30, filter: "blur(8px)" },
  whileInView: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
  },
  viewport: { once: true, margin: "-50px" },
  transition: {
    duration: 0.8,
    ease: [0.16, 1, 0.3, 1],
  },
};

/* ═══════════════════════════════════════════
   8. CARD 3D TILT (enhanced)
   Advanced tilt with shine highlight.
═══════════════════════════════════════════ */
export function useTilt3D(maxTilt = 8) {
  const ref = useRef(null);
  const rotateX = useMotionValue(0);
  const rotateY = useMotionValue(0);
  const shine = useMotionValue("transparent");
  const springRotateX = useSpring(rotateX, { stiffness: 200, damping: 20 });
  const springRotateY = useSpring(rotateY, { stiffness: 200, damping: 20 });

  const handleMouse = useCallback((e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    rotateX.set(-y * maxTilt);
    rotateY.set(x * maxTilt);
    shine.set(`radial-gradient(circle at ${(x + 0.5) * 100}% ${(y + 0.5) * 100}%, rgba(217,119,6,0.06), transparent 60%)`);
  }, [rotateX, rotateY, shine, maxTilt]);

  const handleLeave = useCallback(() => {
    rotateX.set(0);
    rotateY.set(0);
    shine.set("transparent");
  }, [rotateX, rotateY, shine]);

  return {
    ref,
    style: {
      rotateX: springRotateX,
      rotateY: springRotateY,
      transformPerspective: 800,
      transformStyle: "preserve-3d",
    },
    shineStyle: { background: shine },
    handlers: { onMouseMove: handleMouse, onMouseLeave: handleLeave },
  };
}

/* ═══════════════════════════════════════════
   9. SCROLL PROGRESS BAR
   Tracks page scroll for progress indicators.
═══════════════════════════════════════════ */
export function useScrollProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });
  return scaleX;
}

/* ═══════════════════════════════════════════
   10. INTERSECTION-BASED COUNTER
   Animates a number from 0 to target when 
   element comes into view.
═══════════════════════════════════════════ */
export function useCountUp(target, duration = 2000) {
  const ref = useRef(null);
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started) setStarted(true);
    }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const steps = Math.ceil(duration / 16);
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = Math.min(step / steps, 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * target));
      if (progress >= 1) clearInterval(timer);
    }, 16);
    return () => clearInterval(timer);
  }, [started, target, duration]);

  return { ref, count };
}

/* ═══════════════════════════════════════════
   11. TEXT SPLIT ANIMATION
   Splits text into words for stagger animation.
═══════════════════════════════════════════ */
export const wordStagger = {
  container: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.04, delayChildren: 0.1 },
    },
  },
  word: {
    hidden: { opacity: 0, y: 20, filter: "blur(4px)" },
    visible: {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
    },
  },
};

/* ═══════════════════════════════════════════
   12. MAGNETIC HOVER
   Elements that follow the cursor magnetically.
═══════════════════════════════════════════ */
export function useMagnetic(strength = 0.3) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 150, damping: 15 });
  const springY = useSpring(y, { stiffness: 150, damping: 15 });

  const handleMouse = useCallback((e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    x.set((e.clientX - rect.left - rect.width / 2) * strength);
    y.set((e.clientY - rect.top - rect.height / 2) * strength);
  }, [x, y, strength]);

  const handleLeave = useCallback(() => {
    x.set(0);
    y.set(0);
  }, [x, y]);

  return {
    ref,
    style: { x: springX, y: springY },
    handlers: { onMouseMove: handleMouse, onMouseLeave: handleLeave },
  };
}

/* ═══════════════════════════════════════════
   13. SMOOTH SCROLL SNAP
   Section-based scroll tracking for 
   navigation highlights.
═══════════════════════════════════════════ */
export function useActiveSection(sectionIds = []) {
  const [active, setActive] = useState("");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActive(entry.target.id);
          }
        });
      },
      { rootMargin: "-40% 0px -40% 0px" }
    );

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [sectionIds]);

  return active;
}

/* ═══════════════════════════════════════════
   14. HORIZONTAL SCROLL SECTION
   Drive horizontal motion from vertical scroll.
═══════════════════════════════════════════ */
export function useHorizontalScroll() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });
  const x = useTransform(scrollYProgress, [0, 1], ["0%", "-100%"]);
  return { ref, x, progress: scrollYProgress };
}
